import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

const ADMIN_EMAILS = [
  "vladimirsalgado@hotmail.com",
  "vladimirsalgado1@gmail.com",
];

function assertAdmin(claims: any) {
  const email = (claims?.email ?? "").toLowerCase();
  if (!ADMIN_EMAILS.includes(email)) {
    throw new Response("Forbidden", { status: 403 });
  }
}

export const checkIsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const email = ((context.claims as any)?.email ?? "").toLowerCase();
    return { isAdmin: ADMIN_EMAILS.includes(email) };
  });

export const getAdminData = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    assertAdmin(context.claims);

    // Fetch auth users (paginated)
    const authUsers: { id: string; email: string | null; email_confirmed_at: string | null }[] = [];
    let page = 1;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { data, error } = await supabaseAdmin.auth.admin.listUsers({ page, perPage: 1000 });
      if (error) throw error;
      data.users.forEach((u) =>
        authUsers.push({
          id: u.id,
          email: u.email ?? null,
          email_confirmed_at: u.email_confirmed_at ?? null,
        }),
      );
      if (data.users.length < 1000) break;
      page += 1;
    }
    const emailMap = new Map(authUsers.map((u) => [u.id, u]));

    const [{ data: profiles }, { data: clubs }, { data: cafes }, { data: events }, { data: inscripciones }, { data: cafesInvitados }] =
      await Promise.all([
        supabaseAdmin.from("profiles").select("*"),
        supabaseAdmin.from("clubs").select("*"),
        supabaseAdmin.from("cafes").select("*"),
        supabaseAdmin.from("events").select("*"),
        supabaseAdmin.from("inscripciones").select("evento_id"),
        supabaseAdmin.from("cafes_invitados").select("*").order("fecha_invitacion", { ascending: false }),
      ]);

    const profilesEnriched = (profiles ?? []).map((p) => ({
      ...p,
      email: emailMap.get(p.id)?.email ?? null,
      email_confirmed: !!emailMap.get(p.id)?.email_confirmed_at,
    }));

    const profileById = new Map(profilesEnriched.map((p) => [p.id, p]));
    const clubById = new Map((clubs ?? []).map((c) => [c.id, c]));
    const cafeById = new Map((cafes ?? []).map((c) => [c.id, c]));

    const eventsPerClub = new Map<string, number>();
    const eventsPerCafe = new Map<string, number>();
    (events ?? []).forEach((e) => {
      if (e.club_id) eventsPerClub.set(e.club_id, (eventsPerClub.get(e.club_id) ?? 0) + 1);
      if (e.cafe_id) eventsPerCafe.set(e.cafe_id, (eventsPerCafe.get(e.cafe_id) ?? 0) + 1);
    });
    const inscritosPerEvent = new Map<string, number>();
    (inscripciones ?? []).forEach((i) => {
      inscritosPerEvent.set(i.evento_id, (inscritosPerEvent.get(i.evento_id) ?? 0) + 1);
    });

    const clubsEnriched = (clubs ?? []).map((c) => {
      const owner = profileById.get(c.owner_id);
      return {
        ...c,
        organizer_name: owner?.full_name ?? "",
        events_count: eventsPerClub.get(c.id) ?? 0,
      };
    });

    const cafesEnriched = (cafes ?? []).map((c) => {
      const owner = profileById.get(c.owner_id);
      return {
        ...c,
        owner_name: owner?.full_name ?? "",
        owner_email: owner?.email ?? emailMap.get(c.owner_id)?.email ?? null,
        events_count: eventsPerCafe.get(c.id) ?? 0,
      };
    });

    const eventsEnriched = (events ?? []).map((e) => {
      const club = e.club_id ? clubById.get(e.club_id) : null;
      const cafe = e.cafe_id ? cafeById.get(e.cafe_id) : null;
      return {
        ...e,
        club_name: club?.name ?? "",
        cafe_name: cafe?.name ?? "",
        city: club?.city ?? cafe?.city ?? "",
        inscritos: inscritosPerEvent.get(e.id) ?? 0,
      };
    });

    // Métricas
    const now = new Date();
    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const eventsThisWeek = (events ?? []).filter((e) => {
      const d = new Date(e.event_date);
      return d >= now && d <= weekFromNow;
    }).length;

    const metrics = {
      runners: profilesEnriched.filter((p) => p.role === "runner").length,
      organizers: profilesEnriched.filter((p) => p.role === "organizer").length,
      cafes: profilesEnriched.filter((p) => p.role === "cafe").length,
      events: (events ?? []).length,
      inscripciones: (inscripciones ?? []).length,
      eventsThisWeek,
    };

    const cafesInvitadosEnriched = (cafesInvitados ?? []).map((c) => {
      const club = c.club_id ? clubById.get(c.club_id) : null;
      return { ...c, club_name: club?.name ?? "" };
    });

    return {
      metrics,
      profiles: profilesEnriched,
      clubs: clubsEnriched,
      cafes: cafesEnriched,
      events: eventsEnriched,
      cafesInvitados: cafesInvitadosEnriched,
    };
  });
